import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getRecentActivity,
  type RecentActivityResponse,
  type WireRecentOrderActivity,
  type WireRecentSignup,
  type WireRecentTransactionActivity,
} from '../api/endpoints.js';
import { useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  IdMono,
  Input,
  LoadingState,
  PageHead,
  Select,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';
import { SoftBreaks } from './user-inspect/SoftBreaks.js';
import '../styles/activity.mobile.css';

const ALL = '__all__';

/**
 * R-ACTIVITY-V1UI — V1 RecentActivities.jsx parity for /admin/activity.
 *
 *  - V1 statusConfig colours (RecentActivities.jsx:21-29): Bekliyor=sarı,
 *    Aktif=MAVİ, Kuyrukta=mor, Tamamlandı=yeşil, İptal=kırmızı,
 *    Süresi Doldu=gri, İade=turuncu. V2 order states fold into those V1
 *    buckets (reserved/assigned/in_delivery → Aktif; delivered/settled →
 *    Tamamlandı; pending_reservation → Bekliyor).
 *  - 5 s "Canlı" auto-refresh (V1 REFRESH_INTERVAL) with the pinging green
 *    dot; paused while the tab is hidden; stale-while-revalidate via
 *    useApiQuery so a poll never blanks the tables (mobile-friendly).
 *  - Relative "Süre" column (şimdi / 45sn / 3dk / 2sa / 1g) with the exact
 *    timestamp on hover — V1 formatRelativeTime.
 *  - Status legend chips with live counts + click-to-filter and a free-text
 *    search (sipariş / kullanıcı / site) — V1 filter panel.
 *  - Mobile: the activity tables reflow to stacked cards (`.tbl-stack`,
 *    styles.css §10) laid out by the page CSS (styles/activity.mobile.css,
 *    scoped under `.act-page`): the three lists become compact cards below
 *    the 1280px desktop (two per row on tablets and narrow laptops). Each
 *    row keeps its coloured status pill on a 360 px screen.
 */
const REFRESH_INTERVAL_MS = 5_000;

interface V1Status {
  readonly label: string;
  readonly tone: BadgeTone;
}

// V1 statusConfig + the V2-internal states folded into V1 buckets.
const V1_STATUS_CONFIG: Record<string, V1Status> = {
  // V1 wire statuses (RecentActivities.jsx statusConfig — colour-exact)
  pending: { label: 'Bekliyor', tone: 'warn' },
  active: { label: 'Aktif', tone: 'info' },
  queued: { label: 'Kuyrukta', tone: 'purple' },
  completed: { label: 'Tamamlandı', tone: 'success' },
  cancelled: { label: 'İptal', tone: 'danger' },
  expired: { label: 'Süresi Doldu', tone: 'neutral' },
  refunded: { label: 'İade', tone: 'orange' },
  // V2 order states → V1 buckets
  pending_reservation: { label: 'Bekliyor', tone: 'warn' },
  reserved: { label: 'Aktif', tone: 'info' },
  assigned: { label: 'Aktif', tone: 'info' },
  in_delivery: { label: 'Aktif', tone: 'info' },
  delivered: { label: 'Tamamlandı', tone: 'success' },
  settled: { label: 'Tamamlandı', tone: 'success' },
  dispute_open: { label: 'İtiraz', tone: 'danger' },
  dispute_resolved: { label: 'İtiraz Kapandı', tone: 'warn' },
  // Wallet / transaction statuses
  posted: { label: 'Tamamlandı', tone: 'success' },
  failed: { label: 'Hata', tone: 'danger' },
  rejected: { label: 'Hata', tone: 'danger' },
  reversed: { label: 'İade', tone: 'orange' },
};

export function v1Status(status: string): V1Status {
  return (
    V1_STATUS_CONFIG[status.toLowerCase()] ?? { label: status, tone: 'neutral' }
  );
}

function V1StatusBadge({ status }: { readonly status: string }): JSX.Element {
  const cfg = v1Status(status);
  // The label sits in its own span so an unknown raw state (the fallback
  // label) can ellipsize inside a desktop table cell; the full raw status
  // stays reachable through the title.
  return (
    <Badge tone={cfg.tone} dot title={status} className="act-status-badge">
      <span className="act-badge-text">{cfg.label}</span>
    </Badge>
  );
}

/**
 * Touch screens (shared [data-touch-detail]): the raw state behind a bucket
 * label ("reserved" vs "in_delivery" behind "Aktif", "failed" vs "rejected"
 * behind "Hata"), which a mouse reads from the badge tooltip. Nothing when
 * the badge already shows the raw state (an unknown status).
 */
function rawStatusDetail(status: string): string | undefined {
  return v1Status(status).label !== status ? status : undefined;
}

// Legend / filter buckets in the V1 panel order. Filtering is by the V1
// bucket LABEL so e.g. "Aktif" covers reserved+assigned+in_delivery.
const LEGEND_BUCKETS: readonly V1Status[] = [
  { label: 'Bekliyor', tone: 'warn' },
  { label: 'Aktif', tone: 'info' },
  { label: 'Kuyrukta', tone: 'purple' },
  { label: 'Tamamlandı', tone: 'success' },
  { label: 'İptal', tone: 'danger' },
  { label: 'Süresi Doldu', tone: 'neutral' },
  { label: 'İade', tone: 'orange' },
];

const TONE_CHIP_CLASS: Record<BadgeTone, string> = {
  neutral: 'badge-neutral',
  success: 'badge-success',
  warn: 'badge-warn',
  danger: 'badge-danger',
  info: 'badge-info',
  purple: 'badge-purple',
  orange: 'badge-orange',
};

/** V1 useNow — a ticking clock for the relative "Süre" column. 5 s tick
 *  (vs V1's 1 s) keeps re-renders battery-friendly on a phone. */
function useNow(intervalMs = 5_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** V1 formatRelativeTime (RecentActivities.jsx:40-49). */
export function fmtRel(iso: string | null | undefined, now: number): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff < 5) return 'şimdi';
  if (diff < 60) return `${diff}sn`;
  if (diff < 3600) return `${Math.floor(diff / 60)}dk`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}sa`;
  return `${Math.floor(diff / 86400)}g`;
}

export function RecentActivityPage(): JSX.Element {
  const [limit, setLimit] = useState(200);
  // R-ACTIVITY-PAGINATION — server-side page index for the orders list so the
  // operator can page back into older orders (API: ?page=N, 90-day window).
  const [page, setPage] = useState(1);
  const [live, setLive] = useState(true);
  const canRead = useCan('activity:read');

  const query = useApiQuery<RecentActivityResponse>(
    (options) => getRecentActivity(options, limit, page),
    [limit, page],
  );

  // V1 auto-refresh: 5 s tick, skipped while the tab is hidden; an extra
  // refresh fires the moment the tab becomes visible again (V1
  // visibilitychange handler). reload() is useCallback-stable, but we go
  // through a ref so a future hooks change can never staleness this timer.
  // In-flight guard (review finding): reload() aborts the previous fetch,
  // so a response consistently slower than the 5 s tick would be aborted
  // forever and the table would never settle — skip the tick while a
  // fetch is still in flight (V1's inFlightRef overlap guard).
  const reloadRef = useRef(query.reload);
  reloadRef.current = query.reload;
  const busyRef = useRef(false);
  busyRef.current = query.refreshing || query.kind === 'loading';
  useEffect(() => {
    if (!live || !canRead) return;
    const run = (): void => {
      if (!document.hidden && !busyRef.current) reloadRef.current();
    };
    const id = window.setInterval(run, REFRESH_INTERVAL_MS);
    const onVis = (): void => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [live, canRead]);

  return (
    <div className="act-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Son Aktiviteler</>}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Son Aktiviteler
            {live ? <span className="live-dot" aria-label="canlı" /> : null}
          </span>
        }
        subtitle="Son 7 günün müşteri aktivitesi (siparişler, yeni kayıtlar, işlemler) + kalıcı admin event projeksiyonu."
        actions={
          <div className="act-head-actions">
            <span
              className="t-xs t-tertiary act-refreshing"
              style={{ opacity: query.refreshing ? 1 : 0, transition: 'opacity .15s ease' }}
            >
              güncelleniyor…
            </span>
            <Button
              variant="secondary"
              data-testid="live-toggle"
              icon={live ? <span className="live-dot" /> : <Icon.Clock size={14} />}
              onClick={() => setLive((v) => !v)}
              title={live ? 'Canlı yenileme açık (5sn) — durdurmak için tıkla' : 'Canlı yenileme kapalı — başlatmak için tıkla'}
            >
              {live ? 'Canlı' : 'Durduruldu'}
            </Button>
            <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => query.reload()}>
              Yenile
            </Button>
          </div>
        }
      />

      <Card className="act-filter-card">
        <div className="act-filter-row">
          <div className="act-filter-field">
            <label htmlFor="limit" className="field-label">Limit</label>
            <Select id="limit" className="act-limit-select" value={limit} onChange={(e) => { setLimit(Number.parseInt(e.target.value, 10)); setPage(1); }}>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </Select>
          </div>
        </div>
      </Card>

      {!canRead ? <ErrorState reason="capability_missing:activity:read" /> : null}
      {canRead && query.kind === 'loading' ? <LoadingState /> : null}
      {canRead && query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={query.reload} /> : null}
      {canRead && query.kind === 'ok' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* D-232 — the separate "Admin event projeksiyonu" panel was removed
              (operator request): the per-order mail-arrival signal now lives
              inline as the "Mail" column on the orders table below. */}
          <RecentOrdersPanel
            orders={query.data.data?.recentOrders ?? []}
            pagination={query.data.data?.pagination}
            page={page}
            onPage={setPage}
          />
          <RecentSignupsPanel users={query.data.data?.recentUsers ?? []} />
          <RecentTransactionsPanel txns={query.data.data?.recentTransactions ?? []} />
        </div>
      ) : null}
    </div>
  );
}

interface OrdersPagination {
  readonly page: number;
  readonly limit: number;
  readonly totalOrders: number;
  readonly hasMore: boolean;
}

function RecentOrdersPanel({
  orders,
  pagination,
  page,
  onPage,
}: {
  readonly orders: readonly WireRecentOrderActivity[];
  readonly pagination?: OrdersPagination;
  readonly page: number;
  readonly onPage: (p: number) => void;
}): JSX.Element {
  const now = useNow();
  const [bucket, setBucket] = useState<string>(ALL);
  const [search, setSearch] = useState('');

  // V1 statusSummary — live counts per legend bucket.
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of orders) {
      const label = v1Status(o.state).label;
      m.set(label, (m.get(label) ?? 0) + 1);
    }
    return m;
  }, [orders]);

  // V1 filteredOrders — bucket filter + free-text search (sipariş id,
  // kullanıcı, site, sku). Client-side over the loaded window.
  const filtered = useMemo(() => {
    let rows = orders;
    if (bucket !== ALL) rows = rows.filter((o) => v1Status(o.state).label === bucket);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (o) =>
          o.orderId.toLowerCase().includes(q) ||
          o.actor.toLowerCase().includes(q) ||
          (o.site ?? '').toLowerCase().includes(q) ||
          (o.sku ?? '').toLowerCase().includes(q) ||
          // TRACK A — also match the alias domain (mail.com / email.com …).
          (o.domain ?? '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [orders, bucket, search]);

  const hasFilter = bucket !== ALL || search.trim() !== '';

  return (
    <Card flush data-testid="recent-orders-panel">
      <CardHeader title={`Son siparişler (7 gün) — ${hasFilter ? `${filtered.length}/${orders.length}` : orders.length}`} />
      <div className="act-orders-toolbar">
        {/* V1 status legend + click-to-filter chips */}
        <div className="act-chips" data-testid="order-status-legend">
          <button
            type="button"
            className={`status-chip ${bucket === ALL ? 'on' : ''}`}
            onClick={() => setBucket(ALL)}
          >
            Tümü {orders.length ? `(${orders.length})` : ''}
          </button>
          {LEGEND_BUCKETS.map((b) => (
            <button
              key={b.label}
              type="button"
              className={`status-chip ${TONE_CHIP_CLASS[b.tone]} ${bucket === b.label ? 'on' : ''}`}
              onClick={() => setBucket(bucket === b.label ? ALL : b.label)}
              data-testid={`order-filter-${b.label}`}
            >
              <span className="badge-dot" aria-hidden="true" />
              {b.label}
              {counts.get(b.label) ? ` (${counts.get(b.label)})` : ''}
            </button>
          ))}
        </div>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sipariş, kullanıcı, site ara…"
          style={{ maxWidth: 420 }}
          data-testid="order-search"
        />
      </div>
      <CardBody tight style={{ padding: filtered.length === 0 ? 20 : 0 }}>
        {filtered.length === 0 ? (
          <EmptyState label={hasFilter ? 'Filtreyle eşleşen sipariş yok.' : 'Son 7 günde sipariş yok.'} />
        ) : (
          // <1280px: each order is a compact card (styles/activity.mobile.css):
          // status + mail + age on the first line, order id + amount on the
          // second, then the labelled Kullanıcı / Site lines.
          <Table dense stack className="tbl-stack-xl act-cards act-orders-tbl">
            <THead>
              <TRow>
                <TH>Durum</TH>
                <TH>Mail</TH>
                <TH>Sipariş</TH>
                <TH>Kullanıcı</TH>
                <TH>Site</TH>
                <TH>Tutar</TH>
                <TH>Süre</TH>
              </TRow>
            </THead>
            <TBody>
              {filtered.map((o) => (
                <TRow key={o.orderId}>
                  <TD data-label="Durum" className="act-top act-o-status" data-touch-detail={rawStatusDetail(o.state)}>
                    <V1StatusBadge status={o.state} />
                  </TD>
                  {/* D-232 — inline "mail/code arrived?" per order. Green
                      "geldi" (+ first-mail relative time) once a verification
                      mail landed; muted "bekliyor" while still waiting. Lets the
                      operator scan orders and tell which will be delivered/
                      charged vs may be refunded at lease expiry — in real time
                      (the panel polls every 5s). */}
                  <TD
                    data-label="Mail"
                    className="act-top act-o-mail"
                    data-touch-detail={o.codeReceived && o.codeReceivedAt ? fmtDate(o.codeReceivedAt) : undefined}
                  >
                    {o.codeReceived ? (
                      <Badge tone="success" dot title={o.codeReceivedAt ? fmtDate(o.codeReceivedAt) : 'mail geldi'}>
                        geldi{o.codeReceivedAt ? ` · ${fmtRel(o.codeReceivedAt, now)}` : ''}
                      </Badge>
                    ) : (
                      <Badge tone="neutral" dot>bekliyor</Badge>
                    )}
                  </TD>
                  <TD data-label="Sipariş" className="act-top act-o-id"><Link to={`/orders/${encodeURIComponent(o.orderId)}`}><IdMono value={o.orderId} /></Link></TD>
                  <TD data-label="Kullanıcı" className="act-o-user act-line cell-wrap">
                    {o.actorId ? (
                      <Link to={`/users/${encodeURIComponent(o.actorId)}`}><SoftBreaks text={o.actor} /></Link>
                    ) : (
                      <span className="t-tertiary"><SoftBreaks text={o.actor} /></span>
                    )}
                  </TD>
                  {/* Single grouping container: the mobile card reflow makes
                      the td a space-between flex row (label + ONE value), so
                      the two spans must not be separate flex items.
                      TRACK A — the alias DOMAIN (@mail.com / @email.com …) is
                      shown under the site so the operator can see which domain
                      each order targeted, INCLUDING cancelled orders (the
                      domain is persisted on the order row, not joined off the
                      now-released alias). Graceful dash when null (older rows). */}
                  <TD data-label="Site" className="act-o-site act-line cell-wrap">
                    <span className="act-site">
                      <span className="act-site-main">
                        {o.site ? <span className="act-site-name">{o.site}</span> : <span className="t-tertiary">—</span>}
                        {(o.sku || o.providerId) ? (
                          <span className="t-xs t-tertiary">
                            {o.sku ?? ''}{o.providerId ? ` · ${o.providerId}` : ''}
                          </span>
                        ) : null}
                      </span>
                      <span className="t-xs t-tertiary" title="Alias alan adı">
                        {o.domain ? `@${o.domain}` : '—'}
                      </span>
                    </span>
                  </TD>
                  <TD data-label="Tutar" className="t-mono t-num t-price act-top act-o-amount">
                    {fmtChargeUsd(o.amountMicro, o.amountMinor)}
                  </TD>
                  <TD
                    data-label="Süre"
                    className="t-mono t-xs t-tertiary act-top act-o-age"
                    title={fmtDate(o.createdAt)}
                    data-touch-detail={fmtDate(o.createdAt)}
                  >
                    {fmtRel(o.createdAt, now)}
                  </TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
      {/* R-ACTIVITY-PAGINATION — server-side page nav so the operator can go back
          into older orders (90-day window). Client filter/search still apply to the
          loaded page. */}
      {pagination && pagination.totalOrders > 0 ? (
        <div
          className="activity-pager"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}
        >
          <span className="t-xs t-tertiary">
            Sayfa {pagination.page} · ~{pagination.totalOrders.toLocaleString('tr-TR')} sipariş (son 90 gün)
          </span>
          <div className="act-pager-nav">
            <Button variant="secondary" disabled={page <= 1} onClick={() => onPage(Math.max(1, page - 1))}>
              ← Önceki
            </Button>
            <Button variant="secondary" disabled={!pagination.hasMore} onClick={() => onPage(page + 1)}>
              Sonraki →
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Presentation only (desktop sign-ups table): a value with an unbroken run
 * longer than 60 characters gets `.cell-wrap`, so it wraps inside its cell
 * instead of widening the table past its card. Shorter values (real
 * e-mails / user names) keep their natural width, so the column widths
 * and line breaks of a table that fits stay exactly as they are.
 */
function wrapIfLong(value: string | null | undefined): string {
  return value && value.split(/[\s-]+/).some((w) => w.length > 60) ? ' cell-wrap' : '';
}

function RecentSignupsPanel({ users }: { readonly users: readonly WireRecentSignup[] }): JSX.Element {
  return (
    <Card flush data-testid="recent-signups-panel">
      <CardHeader title={`Son kayıtlar — ${users.length}`} />
      <CardBody tight style={{ padding: users.length === 0 ? 20 : 0 }}>
        {users.length === 0 ? (
          <EmptyState label="Yeni kayıt yok." />
        ) : (
          // <1280px: compact cards — e-mail + status on the first line, then
          // the labelled user name / sign-up time lines.
          <Table dense stack className="tbl-stack-xl act-cards act-signups-tbl">
            <THead>
              <TRow>
                <TH>E-posta</TH>
                <TH>Kullanıcı adı</TH>
                <TH>Durum</TH>
                <TH>Kayıt zamanı</TH>
              </TRow>
            </THead>
            <TBody>
              {users.map((u) => (
                <TRow key={u.actorId}>
                  <TD data-label="E-posta" className={`act-top act-s-email${wrapIfLong(u.email)}`}>
                    <Link to={`/users/${encodeURIComponent(u.actorId)}`}><SoftBreaks text={u.email ?? u.actorId.slice(0, 8)} /></Link>
                  </TD>
                  <TD data-label="Kullanıcı adı" className={`act-s-name act-line${wrapIfLong(u.username ?? u.displayName)}`}><SoftBreaks text={u.username ?? u.displayName ?? '—'} /></TD>
                  {/* V1: aktif=emerald pill, pasif=gri pill (RecentActivities.jsx:82-88) */}
                  <TD data-label="Durum" className="act-top act-s-status">{u.isActive ? <Badge tone="success" dot>aktif</Badge> : <Badge tone="neutral" dot>pasif</Badge>}</TD>
                  <TD data-label="Kayıt zamanı" className="t-mono t-xs t-tertiary act-s-time act-line">{fmtDate(u.createdAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function RecentTransactionsPanel({ txns }: { readonly txns: readonly WireRecentTransactionActivity[] }): JSX.Element {
  return (
    <Card flush data-testid="recent-transactions-panel">
      <CardHeader title={`Son işlemler (7 gün) — ${txns.length}`} />
      <CardBody tight style={{ padding: txns.length === 0 ? 20 : 0 }}>
        {txns.length === 0 ? (
          <EmptyState label="Son 7 günde işlem yok." />
        ) : (
          // <1280px: compact cards — type + status, amount + time, then the
          // labelled Kullanıcı / Açıklama lines.
          <Table dense stack className="tbl-stack-xl act-cards act-txns-tbl">
            <THead>
              <TRow>
                <TH>Tip</TH>
                <TH>Kullanıcı</TH>
                <TH>Tutar</TH>
                <TH>Durum</TH>
                <TH>Açıklama</TH>
                <TH>Zaman</TH>
              </TRow>
            </THead>
            <TBody>
              {txns.map((t) => (
                <TRow key={t.id}>
                  <TD data-label="Tip" className="t-mono t-xs act-top act-t-type cell-wrap"><SoftBreaks text={t.transactionType} /></TD>
                  <TD data-label="Kullanıcı" className="act-t-user act-line cell-wrap"><Link to={`/users/${encodeURIComponent(t.actorId)}`}><SoftBreaks text={t.actor} /></Link></TD>
                  <TD data-label="Tutar" className="t-mono t-num t-price act-top act-t-amount">${t.amount.toFixed(2)} {t.currency}</TD>
                  <TD data-label="Durum" className="act-top act-t-status" data-touch-detail={rawStatusDetail(t.status)}>
                    <V1StatusBadge status={t.status} />
                  </TD>
                  <TD data-label="Açıklama" className="act-t-desc act-line">
                    {t.description ?? '—'}
                  </TD>
                  <TD data-label="Zaman" className="t-mono t-xs t-tertiary act-top act-t-time">{fmtDate(t.createdAt)}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR');
  } catch {
    return iso;
  }
}

/**
 * R-ACTIVITY-MICRO — render the order charge from the exact micro amount
 * (4000 = $0.004) so sub-cent prices show honestly. Falls back to the
 * cent-floored minor (×10000) on older API builds. Whole-cent values keep 2
 * decimals; sub-cent values widen to 3–4 (trailing zeros trimmed).
 */
function fmtChargeUsd(
  amountMicro: number | null | undefined,
  amountMinor: number | null,
): string {
  const micro =
    typeof amountMicro === 'number'
      ? amountMicro
      : amountMinor !== null
        ? amountMinor * 10000
        : null;
  if (micro === null) return '—';
  const usd = micro / 1_000_000;
  const cents = usd * 100;
  const wholeCent = Math.abs(cents - Math.round(cents)) < 1e-9;
  return wholeCent
    ? `$${usd.toFixed(2)}`
    : `$${usd.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
}

